import React, { useState, useEffect } from 'react';
import api from '../api';
import { FaPlus, FaEdit, FaTrash, FaTimes } from 'react-icons/fa';

const CategoriasModal = ({ fecharModal, recarregar }) => {
    const [categorias, setCategorias] = useState([]);
    const [novaCategoria, setNovaCategoria] = useState('');
    const [editando, setEditando] = useState(null);
    const [categoriaEditada, setCategoriaEditada] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        carregarCategorias();
    }, []);

    const carregarCategorias = async () => {
        try {
            setLoading(true);
            const response = await api.get('/fichas/api/fichas-tecnicas/categorias');
            if (response.data.success) {
                setCategorias(response.data.categorias);
            }
        } catch (err) {
            console.error('Erro ao carregar categorias:', err);
            setError('Erro ao carregar categorias');
        } finally {
            setLoading(false);
        }
    };

    const adicionarCategoria = async () => {
        if (!novaCategoria.trim()) {
            setError('Digite o nome da categoria');
            return;
        }

        try {
            const response = await api.post('/fichas/api/fichas-tecnicas/categorias', {
                nome: novaCategoria.trim()
            });

            if (response.data.success) {
                await carregarCategorias();
                setNovaCategoria('');
                setError('');
                recarregar();
            }
        } catch (err) {
            console.error('Erro ao adicionar categoria:', err);
            setError(err.response?.data?.message || 'Erro ao adicionar categoria');
        }
    };

    const iniciarEdicao = (categoria) => {
        setEditando(categoria);
        setCategoriaEditada(categoria);
    };

    const salvarEdicao = async () => {
        if (!categoriaEditada.trim()) {
            setError('Digite o nome da categoria');
            return;
        }

        try {
            const response = await api.put('/fichas/api/fichas-tecnicas/categorias', {
                categoria_antiga: editando,
                categoria_nova: categoriaEditada.trim()
            });

            if (response.data.success) {
                await carregarCategorias();
                setEditando(null);
                setCategoriaEditada('');
                setError('');
                recarregar();
            }
        } catch (err) {
            console.error('Erro ao editar categoria:', err);
            setError(err.response?.data?.message || 'Erro ao editar categoria');
        }
    };

    const excluirCategoria = async (categoria) => {
        if (!window.confirm(`Deseja realmente excluir a categoria "${categoria}"?\n\nAs fichas técnicas com esta categoria ficarão sem categoria.`)) {
            return;
        }

        try {
            const response = await api.delete('/fichas/api/fichas-tecnicas/categorias', {
                data: { nome: categoria }
            });

            if (response.data.success) {
                await carregarCategorias();
                setError('');
                recarregar();
            }
        } catch (err) {
            console.error('Erro ao excluir categoria:', err);
            setError(err.response?.data?.message || 'Erro ao excluir categoria');
        }
    };

    const cancelarEdicao = () => {
        setEditando(null);
        setCategoriaEditada('');
    };

    return (
        <div 
            className="modal show d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={fecharModal}
        >
            <div 
                className="modal-dialog modal-dialog-centered modal-lg"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">🏷️ Gerenciar Categorias</h5>
                        <button 
                            type="button" 
                            className="btn-close"
                            onClick={fecharModal}
                        ></button>
                    </div>

                    <div className="modal-body">
                        {error && (
                            <div className="alert alert-danger alert-dismissible fade show">
                                {error}
                                <button 
                                    type="button" 
                                    className="btn-close"
                                    onClick={() => setError('')}
                                ></button>
                            </div>
                        )}

                        {/* Adicionar Nova Categoria */}
                        <div className="card mb-3">
                            <div className="card-body">
                                <h6 className="card-title mb-3">➕ Adicionar Nova Categoria</h6>
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Nome da categoria..."
                                        value={novaCategoria}
                                        onChange={(e) => setNovaCategoria(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                adicionarCategoria();
                                            }
                                        }}
                                    />
                                    <button 
                                        className="btn btn-success"
                                        onClick={adicionarCategoria}
                                    >
                                        <FaPlus /> Adicionar
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Lista de Categorias */}
                        <div className="card">
                            <div className="card-body">
                                <h6 className="card-title mb-3">
                                    📋 Categorias Cadastradas ({categorias.length})
                                </h6>

                                {loading ? (
                                    <div className="text-center py-3">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Carregando...</span>
                                        </div>
                                    </div>
                                ) : categorias.length === 0 ? (
                                    <div className="alert alert-info mb-0">
                                        Nenhuma categoria cadastrada ainda.
                                    </div>
                                ) : (
                                    <div className="list-group">
                                        {categorias.map((categoria) => (
                                            <div 
                                                key={categoria}
                                                className="list-group-item d-flex justify-content-between align-items-center"
                                            >
                                                {editando === categoria ? (
                                                    // Modo Edição
                                                    <div className="d-flex align-items-center gap-2 flex-grow-1">
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm"
                                                            value={categoriaEditada}
                                                            onChange={(e) => setCategoriaEditada(e.target.value)}
                                                            onKeyPress={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    salvarEdicao();
                                                                } else if (e.key === 'Escape') {
                                                                    cancelarEdicao();
                                                                }
                                                            }}
                                                            autoFocus
                                                        />
                                                        <button 
                                                            className="btn btn-sm btn-success"
                                                            onClick={salvarEdicao}
                                                        >
                                                            ✓
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-secondary"
                                                            onClick={cancelarEdicao}
                                                        >
                                                            <FaTimes />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    // Modo Visualização
                                                    <>
                                                        <span>{categoria}</span>
                                                        <div className="d-flex gap-2">
                                                            <button 
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => iniciarEdicao(categoria)}
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                            <button 
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => excluirCategoria(categoria)}
                                                            >
                                                                <FaTrash />
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button 
                            type="button" 
                            className="btn btn-secondary"
                            onClick={fecharModal}
                        >
                            Fechar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CategoriasModal;